/**
 * @file Various text utilies.
 */

import { isPrimaryIdentity } from './identity';

/**
 * Converts an array of strings into an Oxford-comma separated list of items.
 *
 * @param {Array<string>} [arr=[]] Array of strings.
 * @param {string} [conjunction=and] Optional conjunction to use at the end ("and", "or", etc).
 * @returns {string} The comma separated sentence, e.g. Helen, Vassilis, and Ioannis.
 */
const arrayToComma = (arr = [], conjunction = 'and') => {
  if (arr.length === 1) {
    return arr[0];
  }

  if (arr.length === 2) {
    return arr.join(` ${conjunction} `);
  }

  return `${arr.slice(0, -1).join(', ')}, ${conjunction} ${arr.slice(-1)}`;
};

/**
 * Converts an array of identities into an Oxford-comma separated string list.
 *
 * @param {Array.<Identity>} [identities=[]] - Array of identities.
 * @param {string} [conjunction=and] - Optional conjunction to use at the end ("and", "or", etc).
 * @returns {string} The comma separated sentence, e.g. Helen, Vassilis, and Ioannis.
 */
const identityArrayToComma = (identities, conjunction = 'and') => {
  const identityNames = identities.map(identity =>
    isPrimaryIdentity(identity) ? 'you' : identity.name,
  );

  return arrayToComma(identityNames, conjunction);
};

const removeSpaces = string => string.replace(/\s/g, '');

const capitalize = string => {
  if (!string) {
    return '';
  }

  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

const removeHashes = string => string && string.replace('#', '');

/**
 * Normalizes a user-typed name to "Capitalized lower-cased" form (trimmed,
 * whole string lower-cased, first letter upper) — e.g. " vArSiTy " → "Varsity".
 * Used so different import paths (roster CSV, tag import, hierarchy import)
 * produce identical tag names.
 *
 * @param {string} value The raw name.
 * @returns {string} The normalized name.
 */
const normalizeCapitalizedName = value =>
  capitalize((value ?? '').toLowerCase().trim());

const sanitizeHashCode = hashCode => removeHashes(removeSpaces(hashCode));

/**
 * Normalizes spaces, ie removes more than 1 sequential spaces.
 *
 * @see {@link https://stackoverflow.com/a/1981366/6267661}
 *
 * @param {string} string The string to space-normalize.
 * @returns {string} The space-normalized string.
 */
const normalizeSpaces = string => string.replace(/\s\s+/g, ' ');

/**
 * Takes a space separated string (like names usually are,
 * e.g. Mary Jane Blige, or Luis Rojas) and tries to determine
 * first name and last name.
 *
 * @param {string} name The name.
 * @returns {{firstName: string, lastName: string}}
 */
const splitToFirstLastName = (name = '') => {
  if (typeof name !== 'string') {
    return { firstName: '', lastName: '' };
  }

  const arr = normalizeSpaces(name.trim()).split(' ');
  const last = arr.pop();

  return {
    firstName: arr.length ? arr.join(' ') : last,
    lastName: arr.length ? last : '',
  };
};

const formatEnumValue = string => {
  return string ? string.split('_').map(capitalize).join(' ') : '';
};

/**
 * Truncates a string (words).
 *
 * @param {string} str The string.
 * @param {number} max Max words.
 * @returns {string}
 */
const truncateText = (str, max = 10) => {
  const array = str.trim().split(' ');
  const ellipsis = array.length > max ? '…' : '';

  return array.slice(0, max).join(' ') + ellipsis;
};

/**
 * Truncates a string (chars).
 *
 * @param {string} str The string.
 * @param {number} limit Max length - not including truncation character(s).
 * @param {string} truncateWith What to append if truncating.
 * @returns {string}
 */
const truncateString = (str, limit = 10, truncateWith = '…') => {
  return str.length > limit ? str.substr(0, limit) + truncateWith : str;
};

/**
 * From an enum string (CAPITAL_LETTERS_UNDERSCORED) returns a properly formatted string.
 *
 * @example
 * enumToString(ALBUM_TYPE); // 'Album Type'
 * @param {String} enumElem The enum element to transform.
 */
const enumToString = enumElem => capitalize(enumElem.replace('_', ' '));

export {
  arrayToComma,
  identityArrayToComma,
  capitalize,
  removeSpaces,
  removeHashes,
  normalizeCapitalizedName,
  sanitizeHashCode,
  splitToFirstLastName,
  normalizeSpaces,
  formatEnumValue,
  truncateText,
  truncateString,
  enumToString,
};
